import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../SEO';
import { theaterEventSchema } from '../../utils/structuredData';

const TheatreAnnouncementLanding = () => {
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:8001' : '');
      if (!backendUrl) throw new Error('Subscription service is currently unavailable.');
      const response = await fetch(`${backendUrl}/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          source: 'theatre_announcement',
          list_ids: [8], // IRP-Theatre-Announcement
          marketing_emails: marketingConsent
        }),
      });

      if (!response.ok) throw new Error('Subscription service is temporarily unavailable.');
      const result = await response.json();
      if (result.success) {
        setSubmitted(true);
        setEmail('');
      } else {
        throw new Error(result.error || 'Subscription failed.');
      }
    } catch (err) {
      setError(err.message || 'Unable to subscribe. Please try again later.');
    }
  };

  const eventData = theaterEventSchema({
    title: "Something Growing - Theatre Production",
    fullDescription: "A theatrical experience in development. Theatre that changes the temperature of a room before the lights even come up. Irreverent, alive, necessary.",
    image: "/images/theatre/theatre-hero.jpg"
  });

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO
        title="Something Growing - Theatre Production Announcement | Infinite Reyn Productions"
        description="We have something growing. A theatrical experience that asks the questions most rooms are too polite to ask. Theatre that changes the temperature of a room. Sign up to be first to know when we announce. From Infinite Reyn Productions."
        canonical="/launch/theatre-announcement"
        ogImage="/images/theatre/mic-drop.jpg"
        keywords="theatre production, Infinite Reyn Productions theatre, new theatrical experience, provocative theatre, theatrical debut"
        structuredData={eventData}
      />
      {/* Section 1 - Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background - Red stage lighting aesthetic */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-950/40 via-black to-black"></div>
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('/images/theatre/theatre-hero.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50"></div>

        {/* Content */}
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h1 className="text-6xl md:text-8xl font-light mb-8 leading-none">
            We have something growing.
          </h1>
          
          <p className="text-xl md:text-2xl tracking-wider text-white/80 mb-4">
            A theatrical experience. Questions most rooms are too polite to ask.
          </p>
          
          <p className="text-sm tracking-[0.3em] text-gold mb-12">
            FROM INFINITE REYN PRODUCTIONS / THEATRE
          </p>

          {/* Email Form */}
          {!submitted ? (
            <div className="max-w-md mx-auto mb-6">
              <form onSubmit={handleSubmit} className="mb-4">
                <div className="flex flex-col md:flex-row gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    required
                    className="flex-1 bg-white/10 border border-white/20 px-6 py-4 text-white placeholder-white/40 focus:border-gold focus:outline-none transition-colors"
                  />
                  <button
                    type="submit"
                    className="bg-gold text-black px-8 py-4 tracking-wider text-sm font-medium hover:bg-gold/90 transition-colors whitespace-nowrap"
                  >
                    I WANT TO KNOW
                  </button>
                </div>
              </form>
              {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
              <label className="flex items-start gap-3 cursor-pointer group text-left">
                <div className="relative mt-1">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 border border-white/20 transition-colors ${marketingConsent ? 'bg-gold border-gold' : 'bg-white/5 group-hover:border-gold/50'}`}>
                    {marketingConsent && (
                      <svg className="w-full h-full text-black p-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-[10px] md:text-xs text-white/50 leading-relaxed group-hover:text-white/70 transition-colors">
                  I agree to receive updates and marketing emails from Infinite Reyn Productions. You can unsubscribe at any time.
                </span>
              </label>
            </div>
          ) : (
            <div className="max-w-md mx-auto mb-6 p-6 border border-gold/50 bg-gold/5">
              <p className="text-gold tracking-wider">You're on the list. Watch your inbox.</p>
            </div>
          )}

          <p className="text-xs text-white/40">
            Be the first to know when we announce. No spam.
          </p>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-60">
          <div className="w-px h-12 bg-gradient-to-b from-transparent to-white/50"></div>
          <span className="text-xs tracking-widest">SCROLL</span>
        </div>
      </section>

      {/* Section 2 - The Hook */}
      <section className="py-32 px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-12 text-center">
            <div className="pb-8 border-b border-white/10">
              <p className="text-lg md:text-xl text-white/80 font-light leading-relaxed">
                First to hear the announcement when we're ready to reveal it.
              </p>
            </div>
            
            <div className="pb-8 border-b border-white/10">
              <p className="text-lg md:text-xl text-white/80 font-light leading-relaxed">
                Behind-the-scenes access as the production develops.
              </p>
            </div>
            
            <div className="pb-8 border-b border-white/10">
              <p className="text-lg md:text-xl text-white/80 font-light leading-relaxed">
                First look at everything from Infinite Reyn Productions —<br className="hidden md:block" />
                books, music, theatre — before anyone else.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 - The Tease */}
      <section className="py-32 px-6 bg-gradient-to-b from-black to-red-950/20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-16">
            <div className="w-full aspect-video bg-black/50 border border-gold/20 overflow-hidden mb-8">
              <img 
                src="/images/theatre/mic-drop.jpg"
                alt="Microphone on stage floor"
                className="w-full h-full object-cover opacity-90"
              />
            </div>
          </div>

          <div className="space-y-8 mb-16">
            <p className="text-3xl md:text-4xl font-light italic leading-tight">
              Some questions don't belong in polite conversation.
            </p>
            
            <p className="text-3xl md:text-4xl font-light italic leading-tight">
              We're asking them anyway.
            </p>

            <div className="w-24 h-px bg-gold mx-auto my-12"></div>

            <p className="text-lg text-white/70 font-light leading-relaxed max-w-2xl mx-auto">
              Theatre that changes the temperature of a room before the lights even come up. 
              Irreverent, alive, necessary. The kind of work that makes you uncomfortable 
              in exactly the right way.
            </p>
          </div>

          {/* CTA */}
          {!submitted ? (
            <div className="max-w-md mx-auto">
              <form onSubmit={handleSubmit} className="mb-4">
                <div className="flex flex-col md:flex-row gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    required
                    className="flex-1 bg-white/10 border border-white/20 px-6 py-4 text-white placeholder-white/40 focus:border-gold focus:outline-none transition-colors"
                  />
                  <button
                    type="submit"
                    className="bg-gold text-black px-12 py-4 tracking-wider text-sm font-medium hover:bg-gold/90 transition-colors whitespace-nowrap"
                  >
                    COUNT ME IN
                  </button>
                </div>
              </form>
              {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
              <label className="flex items-start gap-3 cursor-pointer group text-left">
                <div className="relative mt-1">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 border border-white/20 transition-colors ${marketingConsent ? 'bg-gold border-gold' : 'bg-white/5 group-hover:border-gold/50'}`}>
                    {marketingConsent && (
                      <svg className="w-full h-full text-black p-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-[10px] md:text-xs text-white/50 leading-relaxed group-hover:text-white/70 transition-colors">
                  I agree to receive updates and marketing emails from Infinite Reyn Productions.
                </span>
              </label>
            </div>
          ) : (
            <div className="max-w-md mx-auto p-6 border border-gold/50 bg-gold/5">
              <p className="text-gold tracking-wider">You're on the list.</p>
            </div>
          )}
        </div>
      </section>

      {/* Section 4 - The Imprint */}
      <section className="py-24 px-6 bg-black border-y border-white/10">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-px bg-gold mx-auto mb-8"></div>
          
          <p className="text-lg text-white/70 font-light leading-relaxed mb-6">
            <strong className="text-white font-medium">Infinite Reyn Productions</strong> is a creative house built for stories that refuse to stay in one form. Books. Music. Theatre. One source.
          </p>
          
          <p className="text-sm tracking-[0.3em] text-gold">
            WHERE STORIES TAKE FORM
          </p>
        </div>
      </section>

      {/* Section 5 - Final CTA */}
      <section className="py-32 px-6 bg-black">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-light mb-8">
            Don't miss the announcement.
          </h2>
          
          <p className="text-white/60 mb-12">
            Something is growing. Be there when it arrives.
          </p>

          {!submitted ? (
            <div className="max-w-md mx-auto">
              <form onSubmit={handleSubmit} className="mb-4">
                <div className="flex flex-col md:flex-row gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    required
                    className="flex-1 bg-white/10 border border-white/20 px-6 py-4 text-white placeholder-white/40 focus:border-gold focus:outline-none transition-colors"
                  />
                  <button
                    type="submit"
                    className="bg-gold text-black px-8 py-4 tracking-wider text-sm font-medium hover:bg-gold/90 transition-colors whitespace-nowrap"
                  >
                    NOTIFY ME
                  </button>
                </div>
              </form>
              {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
              <label className="flex items-start gap-3 cursor-pointer group text-left">
                <div className="relative mt-0.5">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 border border-white/20 transition-colors ${marketingConsent ? 'bg-gold border-gold' : 'bg-white/5 group-hover:border-gold/50'}`}>
                    {marketingConsent && (
                      <svg className="w-full h-full text-black p-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-[10px] text-white/40 leading-tight group-hover:text-white/60 transition-colors">
                  I agree to receive communications from IRP.
                </span>
              </label>
            </div>
          ) : (
            <div className="max-w-md mx-auto p-6 border border-gold/50 bg-gold/5">
              <p className="text-gold tracking-wider">You're on the list. Watch your inbox.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 text-sm text-white/40">
          <Link to="/" className="hover:text-white transition-colors">
            ← Back to Infinite Reyn Productions
          </Link>
          <p>© 2026 Infinite Reyn Productions</p>
        </div>
      </footer>
    </div>
  );
};

export default TheatreAnnouncementLanding;
